import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { SlideshowLightbox, initLightboxJS } from "lightbox.js-react";
import ReactMarkdown from "react-markdown";
import { api, transformProject } from "../services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import CallToActionWithModal from "./CallToActionWithModal";
import CTAWhatsApp from "./CTAWhatsApp";
import ResponsiveTable from "./ResponsiveTable";
import { ProjectsGrid } from "./ProjectsGrid";
import { Headline, Paragraph, Section, ContentBlock } from "./ui";

// Image placeholder component for loading states
const ImagePlaceholder = ({ className }) => (
  <div
    className={`${className} bg-gray-200 relative overflow-hidden min-h-[8rem]`}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer bg-[length:200%_100%]" />
  </div>
);

const ProjectDetail = () => {
  const navigate = useNavigate();
  const { id, parentSlug } = useParams(); // This will be either an ID or slug, and potentially a parent slug
  const location = useLocation();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedProjects, setRelatedProjects] = useState([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Fetch related projects based on current project's tags
  const fetchRelatedProjects = async (currentProject) => {
    if (
      !currentProject ||
      !currentProject.tags ||
      currentProject.tags.length === 0
    ) {
      return;
    }

    try {
      // Get all projects to filter by tags
      const allProjectsResponse = await api.getProjects();
      const allTransformed = allProjectsResponse.data
        .map(transformProject)
        .filter(Boolean);

      // Find articles with matching tags (excluding current article)
      const tagMatches = allTransformed.filter((project) => {
        // Skip current article
        if (
          project.slug === currentProject.slug ||
          project.id === currentProject.id
        ) {
          return false;
        }

        // Check for common tags
        return project.tags.some((tag) => currentProject.tags.includes(tag));
      });

      // Sort by number of matching tags (most relevant first)
      tagMatches.sort((a, b) => {
        const aMatchingTags = a.tags.filter((tag) =>
          currentProject.tags.includes(tag)
        ).length;
        const bMatchingTags = b.tags.filter((tag) =>
          currentProject.tags.includes(tag)
        ).length;
        return bMatchingTags - aMatchingTags;
      });

      // Limit to 3 articles
      setRelatedProjects(tagMatches.slice(0, 3));
    } catch (error) {
      console.error("Error fetching related projects:", error);
    }
  };

  // Custom styles for table-like lists
  const listStyles = `
    .table-list-item:nth-child(odd) {
      background-color: #fefce8; /* yellow-50 */
    }
    
    .table-list-item:nth-child(even) {
      background-color: #fef3c7; /* yellow-100 */
    }
    
    .table-list-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      min-height: 3rem;
      border-bottom: 1px solid #fde68a; /* yellow-200 */
    }
    
    .table-list-item:last-child {
      border-bottom: none;
    }
    
    .table-list-item::marker {
      display: none;
    }
    
    .table-list-item::before {
      display: none;
    }
    
    /* Support for key-value pairs with colons */
    .table-list-item strong:first-child {
      flex: 1;
      text-align: left;
      color: #374151; /* gray-700 */
    }
    
    /* If content contains colon, split it visually */
    .table-list-item {
      text-align: left;
    }
  `;

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        let strapiProject = null;

        // First try to fetch by slug
        strapiProject = await api.getProjectBySlug(id);

        // If not found by slug and id looks like a number, try by ID for backward compatibility
        if (!strapiProject && /^\d+$/.test(id)) {
          strapiProject = await api.getProject(id);
        }

        if (strapiProject) {
          const transformedProject = transformProject(strapiProject);
          setProject(transformedProject);
          setImagesLoaded(false); // Reset image loading state for new project
          fetchRelatedProjects(transformedProject); // Call fetchRelatedProjects after project is loaded
        } else {
          setError("Projekt nicht gefunden");
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        setError("Fehler beim Laden des Projekts");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  // Initialize lightbox after images are loaded (moved to image loading effect)

  // Wait for gallery images to actually load
  useEffect(() => {
    if (
      project &&
      project.gallery &&
      project.gallery.length > 1 &&
      !imagesLoaded
    ) {
      // Use gallery.slice(1) to match the renderGallery logic (skip first image)
      const galleryImages = project.gallery.slice(1).slice(0, 3); // First 3 gallery images
      const imagePromises = galleryImages.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Still resolve on error
          img.src = src;
        });
      });

      Promise.all(imagePromises).then(() => {
        setImagesLoaded(true);
        // Initialize lightbox after images are loaded
        requestAnimationFrame(() => {
          initLightboxJS("ch_3QMdj8JAthSyOIuA0GSfVViL", "individual");
        });
      });
    }
  }, [project, imagesLoaded]);

  const handleMoreImagesClick = () => {
    // Find and click on the 4th image (first hidden one) to open lightbox at that position
    const hiddenImage = document.querySelector(".lightbox-grid img.hidden");
    if (hiddenImage) {
      hiddenImage.click();
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Function to render description with embedded gallery and grey sections
  const renderDescriptionWithGallery = (description, gallery) => {
    const galleryPlaceholder = "[GALLERY]";
    const greyStartTag = "[GREY_SECTION]";
    const greyEndTag = "[/GREY_SECTION]";
    const highlightStartTag = "[HIGHLIGHT]";
    const highlightEndTag = "[/HIGHLIGHT]";
    const iconListStartTag = "[ICON_LIST]";
    const iconListEndTag = "[/ICON_LIST]";
    const headlineListStartTag = "[HEADLINE_LIST]";
    const headlineListEndTag = "[/HEADLINE_LIST]";
    const ctaModalPlaceholder = "[CTA_MODAL:";
    const ctaWhatsAppPlaceholder = "[CTA_WHATSAPP]";
    const tableStartTag = "[TABLE]";
    const tableEndTag = "[/TABLE]";

    // Function to render headline list with headline/description format
    const renderHeadlineList = (content) => {
      // Parse the markdown content to extract list items
      const lines = content.trim().split("\n");
      const items = [];

      for (let line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith("-") || trimmedLine.startsWith("*")) {
          // Remove the bullet point and add to items
          items.push(trimmedLine.substring(1).trim());
        } else if (trimmedLine.length > 0) {
          // If it's not empty and doesn't start with bullet, treat as item
          items.push(trimmedLine);
        }
      }

      // Function to parse headline item with headline and description
      const parseHeadlineItem = (text) => {
        // Look for pattern: **text:** description
        const boldColonMatch = text.match(/\*\*(.*?)\*\*:\s*(.*)/);
        if (boldColonMatch) {
          return {
            headline: boldColonMatch[1],
            description: boldColonMatch[2],
          };
        }

        // Fallback: look for any text before colon
        const colonIndex = text.indexOf(":");
        if (colonIndex !== -1) {
          return {
            headline: text.substring(0, colonIndex).replace(/\*\*/g, "").trim(),
            description: text.substring(colonIndex + 1).trim(),
          };
        }

        // No colon found, treat entire text as description
        return {
          headline: null,
          description: text,
        };
      };

      return (
        <div className="my-6">
          <ul className="space-y-6">
            {items.map((item, index) => {
              const parsed = parseHeadlineItem(item);
              return (
                <li
                  key={index}
                  className="border-l-4 border-yellow-500 px-4 py-3 bg-yellow-50 rounded-r-sm"
                >
                  {parsed.headline && (
                    <h4 className="text-lg font-bold text-gray-900 mb-2 text-left">
                      {parsed.headline}
                    </h4>
                  )}
                  <p className="text-base text-gray-700 leading-relaxed">
                    {parsed.description}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      );
    };

    // Function to render CTA Modal with parsed props
    const renderCtaModal = (propsString) => {
      // Parse props from format: headline|paragraph|darkMode
      const parts = propsString.split("|");
      const headline = parts[0] ? parts[0].trim() : undefined;
      const paragraph = parts[1] ? parts[1].trim() : undefined;
      const darkMode = parts[2]
        ? parts[2].trim().toLowerCase() === "true"
        : false;

      return (
        <CallToActionWithModal
          headline={headline}
          paragraph={paragraph}
          darkMode={true}
          margin={false}
          spacing="compact"
        />
      );
    };

    // Function to render WhatsApp CTA
    const renderCtaWhatsApp = () => (
      <div className="my-8 not-prose">
        <CTAWhatsApp />
      </div>
    );

    // Function to render responsive table
    const renderTable = (content) => (
      <div className="my-8 not-prose">
        <ResponsiveTable content={content} />
      </div>
    );

    // Function to render icon list
    const renderIconList = (content) => {
      // Parse the markdown content to extract list items
      const lines = content.trim().split("\n");
      const items = [];

      for (let line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith("-") || trimmedLine.startsWith("*")) {
          // Remove the bullet point and add to items
          items.push(trimmedLine.substring(1).trim());
        } else if (trimmedLine.length > 0) {
          // If it's not empty and doesn't start with bullet, treat as item
          items.push(trimmedLine);
        }
      }

      // Function to process basic markdown formatting
      const processMarkdown = (text) => {
        return text
          .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Bold
          .replace(/\*(.*?)\*/g, "<em>$1</em>") // Italic
          .replace(/`(.*?)`/g, "<code>$1</code>"); // Code
      };

      return (
        <div className="mt-8 mb-16">
          <ul className="space-y-2">
            {items.map((item, index) => (
              <li
                key={index}
                className="flex items-start gap-3 text-base text-gray-800 leading-relaxed bg-[var(--color-secondary-dark)] p-4 rounded-sm max-w-3xl"
              >
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="text-[var(--color-success)] w-5 h-5 mt-1 flex-shrink-0"
                />
                <span
                  dangerouslySetInnerHTML={{ __html: processMarkdown(item) }}
                />
              </li>
            ))}
          </ul>
        </div>
      );
    };

    const markdownComponentsOther = {
      p: ({ node, ...props }) => (
        <Paragraph variant="base" color="primary" className="my-4" {...props} />
      ),
    };

    // Create markdown components that will be reused
    const markdownComponents = {
      h1: ({ node, ...props }) => (
        <Headline
          variant="h1"
          color="secondary"
          className="mt-5 mb-4"
          {...props}
        />
      ),
      h2: ({ node, ...props }) => (
        <Headline
          variant="h2"
          color="secondary"
          className="mt-12 mb-3"
          {...props}
        />
      ),
      h3: ({ node, ...props }) => (
        <Headline
          variant="h3"
          color="secondary"
          className="mb-2 text-left mt-12"
          {...props}
        />
      ),
      h4: ({ node, ...props }) => (
        <Headline
          variant="h4"
          color="secondary"
          className="mt-5 mb-2 first:mt-0"
          {...props}
        />
      ),
      h5: ({ node, ...props }) => (
        <Headline
          variant="h5"
          color="secondary"
          className="mt-3 mb-2 first:mt-0"
          {...props}
        />
      ),
      h6: ({ node, ...props }) => (
        <Headline
          variant="h6"
          color="secondary"
          className="mt-3 mb-2 first:mt-0"
          {...props}
        />
      ),
      p: ({ node, ...props }) => (
        <Paragraph
          variant="base"
          color="secondary"
          className="mb-4"
          {...props}
        />
      ),
      img: ({ node, ...props }) => (
        <img
          className="rounded-sm shadow-md max-w-full h-auto my-4"
          {...props}
        />
      ),
      ul: ({ node, ...props }) => (
        <div className="mt-6 mb-12 overflow-hidden rounded-sm border border-yellow-200 shadow-sm table-list-wrapper">
          <ul
            className="divide-y divide-yellow-200 bg-white table-list"
            {...props}
          />
        </div>
      ),
      ol: ({ node, ...props }) => (
        <div className="my-5 overflow-hidden rounded-sm border border-yellow-200 shadow-sm table-list-wrapper">
          <ol
            className="divide-y divide-yellow-200 bg-white table-list"
            {...props}
          />
        </div>
      ),
      li: ({ node, children, ...props }) => {

        // Extract text content from ReactMarkdown children
        const extractTextContent = (elements) => {
          if (typeof elements === "string") return elements;
          if (Array.isArray(elements)) {
            return elements
              .map((el) => {
                if (typeof el === "string") return el;
                if (el && el.props && el.props.children) {
                  return extractTextContent(el.props.children);
                }
                return "";
              })
              .join("");
          }
          if (elements && elements.props && elements.props.children) {
            return extractTextContent(elements.props.children);
          }
          return "";
        };

        const textContent = extractTextContent(children);

        // Check if content contains key-value structure (text with colon)
        const hasKeyValue = textContent && textContent.includes(":");

        if (hasKeyValue) {
          const colonIndex = textContent.indexOf(":");
          const key = textContent.substring(0, colonIndex).trim();
          const value = textContent.substring(colonIndex + 1).trim();

          console.log("Key-value detected:", { key, value });

          return (
            <li
              className="px-6 py-4 text-gray-800 font-medium table-list-item hover:bg-yellow-200 transition-colors duration-150"
              {...props}
            >
              <span className="font-semibold text-gray-700 flex-shrink-0 min-w-0 mr-4 max-w-[120px] md:max-w-none">
                {key}
              </span>
              <span className="text-gray-800 text-right flex-1">{value}</span>
            </li>
          );
        }

        return (
          <li
            className="px-6 py-4 text-gray-800 font-medium table-list-item hover:bg-yellow-200 transition-colors duration-150"
            {...props}
          >
            <span className="w-full">{children}</span>
          </li>
        );
      },
      strong: ({ node, ...props }) => (
        <strong className="font-semibold" {...props} />
      ),
      em: ({ node, ...props }) => (
        <em className="italic text-gray-800" {...props} />
      ),
      blockquote: ({ node, ...props }) => (
        <blockquote
          className="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-4"
          {...props}
        />
      ),
    };

    // Function to render gallery
    const renderGallery = () => (
      <div className="my-8 not-prose">
        <div className="lightbox-grid grid grid-cols-1 md:grid-cols-2 gap-4">
          {!imagesLoaded ? (
            // Show placeholders while images load
            gallery
              .slice(1)
              .slice(0, 3)
              .map((_, index) => {
                const isFirstGalleryImage = index === 0;
                return (
                  <ImagePlaceholder
                    key={`placeholder-${index}`}
                    className={`w-full rounded-sm object-cover aspect-[4/3] ${
                      isFirstGalleryImage ? "col-span-full" : ""
                    }`}
                  />
                );
              })
          ) : (
            // Show actual slideshow when images are loaded
            <SlideshowLightbox className="contents" showThumbnails={true}>
              {gallery.slice(1).map((image, index) => {
                const isFirstGalleryImage = index === 0;
                const isVisible = index < 3;

                return (
                  <img
                    key={index + 1}
                    className={`w-full object-cover rounded-sm cursor-pointer hover:opacity-90 transition-opacity aspect-[4/3] ${
                      !isVisible ? "hidden" : ""
                    } ${isFirstGalleryImage ? "col-span-full" : ""}`}
                    src={image}
                    alt={`${project.title} - Bild ${index + 2}`}
                  />
                );
              })}
            </SlideshowLightbox>
          )}
        </div>

        {gallery.length > 4 && imagesLoaded && (
          <div className="mt-4 text-center">
            <button
              onClick={handleMoreImagesClick}
              className="text-blue-600 hover:text-blue-800 hover:underline text-sm cursor-pointer bg-transparent border-none"
            >
              +{gallery.length - 4} weitere Bilder in der Galerie
            </button>
          </div>
        )}
      </div>
    );

    // Function to parse content and handle all placeholders
    const parseContent = (content) => {
      const elements = [];
      let remainingContent = content;
      let elementKey = 0;

      while (remainingContent.length > 0) {
        // Find the next placeholder (gallery, grey section start, highlight start, icon list start, headline list start, CTA modal, CTA WhatsApp, or table)
        const galleryIndex = remainingContent.indexOf(galleryPlaceholder);
        const greyStartIndex = remainingContent.indexOf(greyStartTag);
        const highlightStartIndex = remainingContent.indexOf(highlightStartTag);
        const iconListStartIndex = remainingContent.indexOf(iconListStartTag);
        const headlineListStartIndex =
          remainingContent.indexOf(headlineListStartTag);
        const ctaModalIndex = remainingContent.indexOf(ctaModalPlaceholder);
        const ctaWhatsAppIndex = remainingContent.indexOf(
          ctaWhatsAppPlaceholder
        );
        const tableStartIndex = remainingContent.indexOf(tableStartTag);

        // Determine which placeholder comes first (or if none exist)
        let nextPlaceholderIndex = -1;
        let placeholderType = null;

        // Find the earliest placeholder
        const placeholders = [
          { index: galleryIndex, type: "gallery" },
          { index: greyStartIndex, type: "greySection" },
          { index: highlightStartIndex, type: "highlight" },
          { index: iconListStartIndex, type: "iconList" },
          { index: headlineListStartIndex, type: "headlineList" },
          { index: ctaModalIndex, type: "ctaModal" },
          { index: ctaWhatsAppIndex, type: "ctaWhatsApp" },
          { index: tableStartIndex, type: "table" },
        ]
          .filter((p) => p.index !== -1)
          .sort((a, b) => a.index - b.index);

        if (placeholders.length > 0) {
          nextPlaceholderIndex = placeholders[0].index;
          placeholderType = placeholders[0].type;
        }

        if (nextPlaceholderIndex === -1) {
          // No more placeholders, render remaining content
          if (remainingContent.trim()) {
            elements.push(
              <div
                key={elementKey++}
                className="prose prose-lg max-w-none text-gray-800"
              >
                <ReactMarkdown components={markdownComponents} color="primary">
                  {remainingContent}
                </ReactMarkdown>
              </div>
            );
          }
          break;
        }

        // Add content before the placeholder
        const beforePlaceholder = remainingContent.substring(
          0,
          nextPlaceholderIndex
        );
        if (beforePlaceholder.trim()) {
          elements.push(
            <div
              key={elementKey++}
              className="prose prose-lg max-w-none text-gray-800"
            >
              <ReactMarkdown components={markdownComponents}>
                {beforePlaceholder}
              </ReactMarkdown>
            </div>
          );
        }

        if (placeholderType === "gallery") {
          // Add gallery if we have gallery images
          if (gallery && gallery.length > 1) {
            elements.push(<div key={elementKey++}>{renderGallery()}</div>);
          }
          remainingContent = remainingContent.substring(
            nextPlaceholderIndex + galleryPlaceholder.length
          );
        } else if (placeholderType === "greySection") {
          // Handle grey section
          const afterGreyStart = remainingContent.substring(
            nextPlaceholderIndex + greyStartTag.length
          );
          const greyEndIndex = afterGreyStart.indexOf(greyEndTag);

          if (greyEndIndex !== -1) {
            const greyContent = afterGreyStart.substring(0, greyEndIndex);
            if (greyContent.trim()) {
              elements.push(
                <div
                  key={elementKey++}
                  className="my-6 p-6 bg-gray-50 rounded-sm border border-gray-200"
                >
                  <div className="prose prose-lg max-w-none text-gray-800">
                    <ReactMarkdown components={markdownComponents}>
                      {greyContent}
                    </ReactMarkdown>
                  </div>
                </div>
              );
            }
            remainingContent = afterGreyStart.substring(
              greyEndIndex + greyEndTag.length
            );
          } else {
            // No closing tag found, treat as regular content
            remainingContent = remainingContent.substring(
              nextPlaceholderIndex + greyStartTag.length
            );
          }
        } else if (placeholderType === "highlight") {
          // Handle highlight section
          const afterHighlightStart = remainingContent.substring(
            nextPlaceholderIndex + highlightStartTag.length
          );
          const highlightEndIndex =
            afterHighlightStart.indexOf(highlightEndTag);

          if (highlightEndIndex !== -1) {
            const highlightContent = afterHighlightStart.substring(
              0,
              highlightEndIndex
            );
            if (highlightContent.trim()) {
              elements.push(
                <div
                  key={elementKey++}
                  className="mt-10 mb-10 border-l-4 border-red-500 px-6 py-2 bg-red-50"
                >
                  <div className="prose prose-lg max-w-none">
                    <ReactMarkdown components={markdownComponentsOther}>
                      {highlightContent}
                    </ReactMarkdown>
                  </div>
                </div>
              );
            }
            remainingContent = afterHighlightStart.substring(
              highlightEndIndex + highlightEndTag.length
            );
          } else {
            // No closing tag found, treat as regular content
            remainingContent = remainingContent.substring(
              nextPlaceholderIndex + highlightStartTag.length
            );
          }
        } else if (placeholderType === "iconList") {
          // Handle icon list section
          const afterIconListStart = remainingContent.substring(
            nextPlaceholderIndex + iconListStartTag.length
          );
          const iconListEndIndex = afterIconListStart.indexOf(iconListEndTag);

          if (iconListEndIndex !== -1) {
            const iconListContent = afterIconListStart.substring(
              0,
              iconListEndIndex
            );
            if (iconListContent.trim()) {
              elements.push(
                <div key={elementKey++} className="not-prose mb-15">
                  {renderIconList(iconListContent)}
                </div>
              );
            }
            remainingContent = afterIconListStart.substring(
              iconListEndIndex + iconListEndTag.length
            );
          } else {
            // No closing tag found, treat as regular content
            remainingContent = remainingContent.substring(
              nextPlaceholderIndex + iconListStartTag.length
            );
          }
        } else if (placeholderType === "headlineList") {
          // Handle headline list section
          const afterHeadlineListStart = remainingContent.substring(
            nextPlaceholderIndex + headlineListStartTag.length
          );
          const headlineListEndIndex =
            afterHeadlineListStart.indexOf(headlineListEndTag);

          if (headlineListEndIndex !== -1) {
            const headlineListContent = afterHeadlineListStart.substring(
              0,
              headlineListEndIndex
            );
            if (headlineListContent.trim()) {
              elements.push(
                <div key={elementKey++} className="not-prose">
                  {renderHeadlineList(headlineListContent)}
                </div>
              );
            }
            remainingContent = afterHeadlineListStart.substring(
              headlineListEndIndex + headlineListEndTag.length
            );
          } else {
            // No closing tag found, treat as regular content
            remainingContent = remainingContent.substring(
              nextPlaceholderIndex + headlineListStartTag.length
            );
          }
        } else if (placeholderType === "ctaModal") {
          // Handle CTA Modal
          const afterCtaModalStart = remainingContent.substring(
            nextPlaceholderIndex + ctaModalPlaceholder.length
          );
          const closeBracketIndex = afterCtaModalStart.indexOf("]");

          if (closeBracketIndex !== -1) {
            const propsString = afterCtaModalStart.substring(
              0,
              closeBracketIndex
            );
            elements.push(
              <div key={elementKey++}>{renderCtaModal(propsString)}</div>
            );
            remainingContent = afterCtaModalStart.substring(
              closeBracketIndex + 1
            );
          } else {
            // No closing bracket found, treat as regular content
            remainingContent = remainingContent.substring(
              nextPlaceholderIndex + ctaModalPlaceholder.length
            );
          }
        } else if (placeholderType === "ctaWhatsApp") {
          // Handle CTA WhatsApp
          elements.push(<div key={elementKey++}>{renderCtaWhatsApp()}</div>);
          remainingContent = remainingContent.substring(
            nextPlaceholderIndex + ctaWhatsAppPlaceholder.length
          );
        } else if (placeholderType === "table") {
          // Handle Table
          const afterTableStart = remainingContent.substring(
            nextPlaceholderIndex + tableStartTag.length
          );
          const tableEndIndex = afterTableStart.indexOf(tableEndTag);

          if (tableEndIndex !== -1) {
            const tableContent = afterTableStart.substring(0, tableEndIndex);
            if (tableContent.trim()) {
              elements.push(
                <div key={elementKey++}>{renderTable(tableContent)}</div>
              );
            }
            remainingContent = afterTableStart.substring(
              tableEndIndex + tableEndTag.length
            );
          } else {
            // No closing tag found, treat as regular content
            remainingContent = remainingContent.substring(
              nextPlaceholderIndex + tableStartTag.length
            );
          }
        }
      }

      return elements;
    };

    // If no special placeholders, render normally
    if (
      !description.includes(galleryPlaceholder) &&
      !description.includes(greyStartTag) &&
      !description.includes(highlightStartTag) &&
      !description.includes(iconListStartTag) &&
      !description.includes(headlineListStartTag) &&
      !description.includes(ctaModalPlaceholder) &&
      !description.includes(ctaWhatsAppPlaceholder) &&
      !description.includes(tableStartTag)
    ) {
      return (
        <div className="prose prose-lg max-w-none text-gray-800">
          <ReactMarkdown components={markdownComponents}>
            {description}
          </ReactMarkdown>
        </div>
      );
    }

    // Parse and render content with placeholders
    const elements = parseContent(description);

    return <div>{elements}</div>;
  };

  // Show error state only when loading is complete and there's an error or no project
  if (!loading && (error || !project)) {
    return (
      <Section variant="primary" spacing="comfortable" className="min-h-screen">
        <ContentBlock maxWidth="4xl" spacing="comfortable" className="mx-auto">
          <div className="text-center py-20">
            <Paragraph variant="base" className="text-red-600 mb-4">
              {error || "Projekt nicht gefunden"}
            </Paragraph>
            <button
              onClick={handleBackClick}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Zurück zu den Projekten
            </button>
          </div>
        </ContentBlock>
      </Section>
    );
  }

  // Don't render anything while loading (matches OfferDetail approach)
  if (!project) {
    return null;
  }

  return (
    <>
      {/* Inject custom styles for table-like lists */}
      <style dangerouslySetInnerHTML={{ __html: listStyles }} />

      {/* Full-width hero image */}
      {/* {project.image && (
        <div className="relative h-64 md:h-96 w-full overflow-hidden">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover"
          />
        </div>
      )} */}

      {/* Content section */}
      <Section
        variant="primary"
        spacing="comfortable_top_none_bottom"
        margin={false}
      >
        <ContentBlock maxWidth="3xl" spacing="comfortable" className="mx-auto">
          <div className="overflow-hidden">
            <div className="pt-0">
              <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                <div className="flex items-center gap-4 my-6">
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: "var(--color-highlight)" }}
                  >
                    {project.category}
                  </span>
                  <time dateTime={project.date}>
                    {formatDate(project.date)}
                  </time>
                </div>
                <button
                  onClick={handleBackClick}
                  className="text-blue-600 hover:underline text-sm inline-block bg-transparent border-none cursor-pointer"
                >
                  {location.pathname.includes("/verwandte-artikel/")
                    ? "← Zurück"
                    : "← Zurück"}
                </button>
              </div>
              <Headline variant="h2" color="secondary" className="mb-6">
                {project.title}
              </Headline>

              {/* Render description with embedded gallery */}
              {renderDescriptionWithGallery(
                project.description,
                project.gallery
              )}
            </div>
          </div>
        </ContentBlock>
      </Section>

      {/* Related Articles Section */}
      {relatedProjects.length > 0 && (
        <ProjectsGrid
          showAll={false}
          showHeader={true}
          showIntro={true}
          header="Das könnte Sie auch interessieren"
          introText="Entdecken Sie verwandte Themen und Projekte"
          margin={false}
          // Pass the related projects directly as data
          postSlugs={relatedProjects.map((p) => p.slug).filter(Boolean)}
        />
      )}
    </>
  );
};

export default ProjectDetail;
